import React, { useState } from 'react'
import {
	EuiButton,
	EuiCallOut,
	EuiFieldText,
	EuiFormRow,
	EuiModalBody,
	EuiModalFooter,
	EuiSpacer,
} from '@equipmentshare/ds2'
import ToEntity, { ToEntityType } from './components/ToEntity'
import FromEntity, { FromEntityType } from './components/FromEntity'
import AddPartsForm, { TransactionItemFormState, getEmptyItem } from './components/AddPartsForm'
import { getRefetchTransactionQueries } from '../../utils'
import { useCreateTransactionMutation, TransactionType, TransactionStatus } from '../../../../graphql'
import {
	isFromProvider,
	isFromStore,
	validateFromId,
	validateToId,
} from '../../../../graphql/utils/transaction-types'
import { useShowSuccess, useShowError } from 'hooks/useToast'
import validateForm, { SetUnexpectedCost } from './helpers/validateForm'

export type TransactionFormState = {
	fromEntity?: FromEntityType,
	toEntity?: ToEntityType,
	transactionItems: TransactionItemFormState[],
	memo: string,
	customId: string,
	validation: {
		fromEntity: {
			isInvalid: boolean,
			error?: string,
		},
		toEntity: {
			isInvalid: boolean,
			error?: string,
		},
		items: {
			isInvalid: boolean,
			error?: string,
		},
	},
}

export type UnexpectedItemCost = {
	[ key: number ]: 'high' | 'low',
}

export type SubmittedTransactionItem = {
	partId: number,
	quantity: number,
	costPerItem: number,
}

export type TransactionFormProps = {
	onCancel: () => void,
	onSuccess: () => void,
	onSubmitClick: () => void,
	transactionType: TransactionType,
	defaultFromId?: number,
	defaultToId?: number,
	fromId?: number,
	toId?: number,
	defaultMemo?: string,
	initialLineItems?: TransactionItemFormState[],
	onSubmitSuccess?: ( submittedItems: SubmittedTransactionItem[], transactionId?: number ) => void,
}

export function extractId( givenEntity?: { id?: number | string } | null ) {
	if ( typeof givenEntity?.id === 'number' ) {
		return givenEntity.id
	} else if ( typeof givenEntity?.id === 'string' ) {
		return parseInt( givenEntity.id, 10 )
	}
	return undefined
}

export default function TransactionForm( { onCancel, onSuccess, onSubmitClick, transactionType, defaultFromId, defaultToId, fromId, toId, defaultMemo, initialLineItems, onSubmitSuccess }: TransactionFormProps ) {
	const showError = useShowError()
	const showSuccess = useShowSuccess()
	const [ isSaving, setIsSaving ] = useState( false )
	const [ formState, setFormState ] = useState<TransactionFormState>( {
		fromEntity: undefined,
		toEntity: undefined,
		transactionItems: initialLineItems ?? [ getEmptyItem( 0 ) ],
		memo: defaultMemo ?? '',
		customId: '',
		validation: {
			fromEntity: { isInvalid: false },
			toEntity: { isInvalid: false },
			items: { isInvalid: false },
		}
	} )
	const [ unexpectedItemCost, setUnexpectedItemCost ] = useState<UnexpectedItemCost>( {} )
	const [ createTransaction ] = useCreateTransactionMutation( { refetchQueries: [ ...getRefetchTransactionQueries(), 'StoreParts' ] } )

	const handleFieldChange = ( fieldName: string ) => ( event: React.ChangeEvent<HTMLInputElement> ) => {
		const value = event.currentTarget.value
		setFormState( ( prev ) => ( {
			...prev,
			[ fieldName ]: value,
		} ) )
	}

	const setItems = ( transactionItems: TransactionItemFormState[] | ((prev: TransactionItemFormState[]) => TransactionItemFormState[]) ) => (
		setFormState( ( prev ) => ( {
			...prev,
			transactionItems: typeof transactionItems === 'function' ? transactionItems( prev.transactionItems ) : transactionItems,
			validation: {
				...prev.validation,
				items: { isInvalid: false },
			}
		} ) )
	)

	const handleSelectFromEntity = ( _: number, fromEntity?: FromEntityType ) => (
		setFormState( ( prev ) => ( {
			...prev,
			fromEntity,
			validation: {
				...prev.validation,
				fromEntity: { isInvalid: false },
			}
		} ) )
	)

	const handleSelectToEntity = ( _: number, toEntity?: ToEntityType ) => (
		setFormState( ( prev ) => ( {
			...prev,
			toEntity,
			validation: {
				...prev.validation,
				toEntity: { isInvalid: false },
			}
		} ) )
	)

	const setUnexpectedCost: SetUnexpectedCost = ( index, flag ) => {
		if ( flag === 'normal' ) {
			// remove this one from the list
			setUnexpectedItemCost( ( prev ) => {
				const newUnexpectedCost = { ...prev }
				delete newUnexpectedCost[ index ]
				return newUnexpectedCost
			} )
		} else {
			setUnexpectedItemCost( ( prev ) => ( {
				...prev,
				[ index ]: flag,
			} ) )
		}
	}

	const ignoreUnexpectedCost = () => {
		setUnexpectedItemCost( {} )
		setFormState( ( prev ) => ( {
			...prev,
			transactionItems: prev.transactionItems.map( ( item ) => (
				{
					...item,
					validation: {
						...item.validation,
						costPerItem: { isInvalid: false },
					}
				}
			) )
		} ) )
	}

	const handleSubmit = async() => {
		if ( Object.keys( unexpectedItemCost ).length === 0 ) {
			onSubmitClick()
			setIsSaving( true )
			const { fromEntity, toEntity, transactionItems, memo, customId } = formState

			const validFromId = fromId ?? extractId( fromEntity ) ?? defaultFromId
			const validToId = toId ?? extractId( toEntity ) ?? defaultToId

			const { newFormState, hasErrors } = validateForm( formState, transactionType, validFromId, validToId )

			if ( hasErrors ) {
				setFormState( newFormState )
				setIsSaving( false )
			} else {
				const variables = {
					transactionType,
					transactionStatus: TransactionStatus.Submitted,
					fromId: validFromId,
					toId: validToId,
					transactionItems: transactionItems.map( ( { costPerItem, quantity, part, storePart } ) => ( {
						costPerItem: parseFloat( costPerItem ),
						quantity: parseInt( quantity, 10 ),
						partId: storePart?.part?.id ?? part?.id ?? -1,
					} ) ),
					memo: memo.length ? memo : undefined,
					customId: customId.length ? customId : undefined,
				}

				try {
					const result = await createTransaction( { variables } )
					setIsSaving( false )
					showSuccess( `transactionSuccessful${Date.now()}`, 'Action successful' )

					// Call onSubmitSuccess with the submitted items and transaction ID before onSuccess
					if ( onSubmitSuccess ) {
						const transactionId = result.data?.createTransaction?.id
						onSubmitSuccess( variables.transactionItems, transactionId )
					}

					onSuccess()
				} catch ( e: any ) {
					setIsSaving( false )
					showError( `transactionError${Date.now()}`, e.message )
				}
			}
		}
	}

	const shouldAddPartsFormBeDisabled = () => {
		const { toEntity, fromEntity } = formState

		const validToId = extractId( toEntity ) ?? toId ?? defaultToId
		const validFromId = extractId( fromEntity ) ?? fromId ?? defaultFromId

		return !validateToId( transactionType, validToId ) || !validateFromId( transactionType, validFromId )
	}

	const storeId = isFromStore( transactionType ) ? extractId( formState.fromEntity ) ?? defaultFromId : undefined

	const hasValidationErrors = () => {
		// Check if form-level validation has errors
		if ( formState.validation.fromEntity.isInvalid ||
			 formState.validation.toEntity.isInvalid ||
			 formState.validation.items.isInvalid ) {
			return true
		}

		// Check if any transaction items have validation errors
		const hasItemErrors = formState.transactionItems.some( ( item ) =>
			item.validation.quantity.isInvalid ||
			item.validation.costPerItem.isInvalid ||
			item.validation.part.isInvalid )

		// Check if any items are unavailable (not in stock)
		const hasUnavailableItems = formState.transactionItems.some( ( item ) => item.isUnavailable )

		return hasItemErrors || hasUnavailableItems
	}

	const hasUnavailableItems = formState.transactionItems.some( ( item ) => item.isUnavailable )

	return (
		<>
			<EuiModalBody>
				{ hasUnavailableItems && (
					<>
						<EuiCallOut
							color="warning"
							title="Unavailable Items"
						>
							Some items are not in stock at the fulfilling inventory. Please remove unavailable items to continue.
						</EuiCallOut>
						<EuiSpacer size="m" />
					</>
				) }
				{ Object.keys( unexpectedItemCost ).length > 0 && (
					<>
						<EuiCallOut
							color="danger"
							title="Price Adjustment"
						>
							{ Object.entries( unexpectedItemCost ).map( ( [ index, flag ] ) => {
								const key = `adjust-item-${index}`
								const item = formState.transactionItems.find( ( value ) => value.idx.toString() === index )
								if ( item ) {
									const partNumber = item.part ? item.part?.partNumber : item.storePart?.part?.partNumber
									const direction = flag === 'high' ? 'higher' : 'lower'
									return (
										<div key={key}>
											The price entered below for item #{partNumber} is {direction} than our recorded price.
										</div>
									)
								}
								return <div key={key} />
							} ) }
							<div>If this is correct, click &lsquo;Ignore&rsquo; below to continue. Otherwise, adjust accordingly.</div>
							<EuiSpacer size="m" />
							<EuiButton
								onClick={ignoreUnexpectedCost}
								color="danger"
								id="ignoreUnexpectedCost"
							>
								Ignore
							</EuiButton>
						</EuiCallOut>
						<EuiSpacer size="m" />
					</>
				) }
				<FromEntity
					type={transactionType}
					selectedItem={formState.fromEntity}
					selectedItemId={defaultFromId}
					onChange={handleSelectFromEntity}
					formRowProps={formState.validation.fromEntity}
				/>
				<ToEntity
					type={transactionType}
					selectedItem={formState.toEntity}
					selectedItemId={defaultToId}
					onChange={handleSelectToEntity}
					formRowProps={formState.validation.toEntity}
				/>
				<EuiSpacer />
				<div className="scrollModalBody">
					<AddPartsForm
						isDisabled={shouldAddPartsFormBeDisabled()}
						items={formState.transactionItems}
						setItems={setItems}
						storeId={storeId}
						validation={formState.validation.items}
						setUnexpectedCost={setUnexpectedCost}
						toId={extractId( formState.toEntity )}
					/>
				</div>
				<EuiSpacer />
				<EuiFormRow
					label="Memo"
				>
					<EuiFieldText
						id="memo"
						onChange={handleFieldChange( 'memo' )}
						placeholder="A short description..."
						value={formState.memo}
					/>
				</EuiFormRow>

				{isFromProvider( transactionType ) && (
					<EuiFormRow
						label="Purchase Order ID"
					>
						<EuiFieldText
							id="customId"
							onChange={handleFieldChange( 'customId' )}
							value={formState.customId}
						/>
					</EuiFormRow>
				) }
			</EuiModalBody>
			<EuiModalFooter>
				<EuiButton
					color="text"
					onClick={onCancel}
				>
					Cancel
				</EuiButton>
				<EuiButton
					id="transaction-form-submit-button"
					fill
					isLoading={isSaving}
					isDisabled={hasValidationErrors()}
					onClick={handleSubmit}
				>
					Submit
				</EuiButton>
			</EuiModalFooter>
		</>
	)
}
